import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { projectTagsLoad } from 'portal/store/events/actions'
import { useModuleIds } from './useModuleIds'

type ProjectTagsState = {
  loading: boolean
  loaded: boolean
  error: unknown
  items: string[]
}

const EMPTY_PROJECT_TAGS_STATE: ProjectTagsState = {
  loading: false,
  loaded: false,
  error: null,
  items: []
}

/**
 * Loads and caches a project's tags, keyed by account/project.
 *
 * accountId and projectId come from the current route (via useModuleIds()) - but projectId
 * can be overridden for pages where the project comes from state (i.e. AccountSettings)
 */
export function useProjectTags(projectIdOverride?: string) {
  const dispatch = useDispatch()
  const { accountId, projectId: routeProjectId } = useModuleIds()
  const projectId = projectIdOverride ?? routeProjectId

  const projectKey = useMemo(
    () => `${accountId}_${projectId}`,
    [accountId, projectId]
  )

  const projectTagsState: ProjectTagsState = useSelector((state: any) =>
    state.events?.projectTags?.[projectKey]
      ? state.events.projectTags[projectKey]
      : EMPTY_PROJECT_TAGS_STATE
  )

  useEffect(() => {
    if (!accountId || !projectId) {
      return
    }

    if (!projectTagsState.loaded && !projectTagsState.loading) {
      dispatch(projectTagsLoad(accountId, projectId))
    }
  }, [
    accountId,
    dispatch,
    projectId,
    projectTagsState.loaded,
    projectTagsState.loading
  ])

  return {
    projectTags: projectTagsState.items || [],
    loading: projectTagsState.loading,
    loaded: projectTagsState.loaded,
    error: projectTagsState.error,
    reload: () => {
      if (!accountId || !projectId) {
        return
      }

      dispatch(projectTagsLoad(accountId, projectId))
    }
  }
}
